import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useTenant } from '../../contexts/TenantContext';

export default function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { addDebugLog } = useTenant();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  
  // Magic Link State
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);

  // CRITICAL: Prevent multiple Magic Link callback processing
  const magicLinkProcessedRef = useRef(false);
  const isProcessingRegisterRef = useRef(false);

  // KRITISCH: Prüfe ob wir von einem Magic Link kommen
  useEffect(() => {
    const checkMagicLinkCallback = async () => {
      // CRITICAL: Only process once
      if (magicLinkProcessedRef.current) {
        console.log('[Register] ⚠️ Magic Link bereits verarbeitet - überspringe');
        return;
      }

      // Prüfe URL-Hash für Supabase Auth Callback
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const type = hashParams.get('type');
      
      if (accessToken && type === 'magiclink') {
        // Mark as processed immediately
        magicLinkProcessedRef.current = true;

        console.log('[Register] 🎯 Magic Link Callback erkannt!');
        addDebugLog('success', 'auth', '[Register] 🎯 Magic Link Callback erkannt!', {
          type,
          has_token: !!accessToken
        });
        
        try {
          // Entferne Hash aus URL sofort
          console.log('[Register] 🧹 Bereinige URL-Hash...');
          addDebugLog('info', 'auth', '[Register] 🧹 Bereinige URL-Hash...');
          window.history.replaceState(null, '', window.location.pathname);
          
          // Warte kurz auf Session-Initialisierung
          console.log('[Register] ⏳ Warte auf Session...');
          addDebugLog('info', 'auth', '[Register] ⏳ Warte auf Session...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Prüfe Session
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError || !session) {
            console.error('[Register] ❌ Session-Fehler nach Magic Link');
            addDebugLog('error', 'auth', '[Register] ❌ Session-Fehler nach Magic Link');
            setError('Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
            magicLinkProcessedRef.current = false; // Reset on error
            return;
          }
          
          console.log('[Register] ✅ Session vorhanden - Bootstrap wird automatisch starten');
          addDebugLog('success', 'auth', '[Register] ✅ Session vorhanden - Bootstrap wird automatisch starten');
          
          // Let TenantContext handle the redirect after bootstrap
          
        } catch (err: any) {
          console.error('[Register] ❌ Magic Link Callback Error:', err);
          addDebugLog('error', 'auth', '[Register] ❌ Magic Link Callback Error', {
            error: err.message
          });
          setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
          magicLinkProcessedRef.current = false; // Reset on error
        }
      }
    };
    
    checkMagicLinkCallback();
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // CRITICAL: Prevent multiple submissions
    if (isProcessingRegisterRef.current) {
      console.log('[Register] ⚠️ Registrierung bereits in Bearbeitung - überspringe');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    if (password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    isProcessingRegisterRef.current = true;
    setError('');
    setMagicLinkSent(false);
    setLoading(true);

    try {
      console.log(`[Register] 🔐 Step 1/3: Starting password registration for: ${email}`);
      addDebugLog('info', 'auth', `[Register] 🔐 Step 1/3: Starting password registration for: ${email}`);

      // STEP 1: Call the register-user Edge Function
      console.log('[Register] 📡 Step 2/3: Calling register-user Edge Function...');
      addDebugLog('info', 'auth', '[Register] 📡 Step 2/3: Calling register-user Edge Function...');
      
      const { data: registerData, error: functionError } = await supabase.functions.invoke('register-user', {
        body: {
          email,
          password,
          confirmPassword,
        },
      });

      if (functionError) {
        console.error('[Register] ❌ Edge Function Error:', functionError);
        addDebugLog('error', 'auth', '[Register] ❌ Edge Function Error', functionError);
        throw functionError;
      }

      // Check if user already exists and magic link was sent
      if (registerData.user_exists && registerData.magic_link_sent) {
        console.log('[Register] ℹ️ User exists - magic link sent');
        addDebugLog('info', 'auth', '[Register] ℹ️ User exists - magic link sent', {
          email: email,
          email_id: registerData.email_id
        });
        
        // Show success message about magic link
        setError('');
        setRegisteredEmail(email);
        setMagicLinkSent(true);
        setLoading(false);
        isProcessingRegisterRef.current = false;
        return;
      }

      // Check if registration failed
      if (!registerData.success) {
        console.warn('[Register] ⚠️ Registration failed:', registerData.message);
        addDebugLog('warning', 'auth', '[Register] ⚠️ Registration failed', registerData.message);
        throw new Error(registerData.error || 'Registration failed');
      }

      // STEP 2: Registration successful - magic link sent
      console.log('[Register] ✅ Step 3/3: Registration successful! Magic link sent.');
      addDebugLog('success', 'auth', '[Register] ✅ Step 3/3: Registration successful! Magic link sent.', {
        user_id: registerData.user?.id,
        email: registerData.user?.email,
        magic_link_sent: registerData.magic_link_sent
      });
      
      // Show success state
      setRegisteredEmail(email);
      setMagicLinkSent(true);
      setError('');

    } catch (err: any) {
      console.error('[Register] ❌ Error:', err);
      addDebugLog('error', 'auth', '[Register] ❌ Error', {
        message: err.message,
        stack: err.stack
      });
      setError(err.message || 'Registrierung fehlgeschlagen');
      setMagicLinkSent(false);
    } finally {
      setLoading(false);
      isProcessingRegisterRef.current = false;
    }
  };

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMagicLinkSent(false);
    setMagicLinkLoading(true);

    try {
      console.log(`[Register] 📧 Calling send-magic-link Edge Function for registration: ${magicLinkEmail}`);
      addDebugLog('info', 'auth', `[Register] 📧 Calling send-magic-link Edge Function for registration: ${magicLinkEmail}`);
      
      // Get Supabase URL from environment
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/send-magic-link`;
      
      console.log(`[Register] 🔗 Edge Function URL: ${edgeFunctionUrl}`);
      addDebugLog('info', 'auth', `[Register] 🔗 Edge Function URL: ${edgeFunctionUrl}`);
      
      // Call custom Edge Function with isRegistration flag
      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: magicLinkEmail,
          isRegistration: true, // ✅ Tell the function this is a registration
        }),
      });

      const responseData = await response.json();
      
      console.log('[Register] 📧 Edge Function Response:', responseData);
      addDebugLog('info', 'auth', '[Register] 📧 Edge Function Response', {
        status: response.status,
        ok: response.ok,
        data: responseData
      });

      if (!response.ok) {
        console.error('[Register] ❌ Edge Function Error:', responseData);
        addDebugLog('error', 'auth', '[Register] ❌ Edge Function Error - FULL DETAILS', {
          status: response.status,
          error: responseData.error,
          debug: responseData.debug,
          full_response: JSON.stringify(responseData, null, 2)
        });
        
        // Show detailed error to user
        const errorMessage = responseData.error || 'Failed to send magic link';
        const debugInfo = responseData.debug ? `\n\nDebug Info:\n${JSON.stringify(responseData.debug, null, 2)}` : '';
        
        throw new Error(`❌ ${errorMessage}${debugInfo}`);
      }

      console.log('[Register] ✅ Magic Link sent successfully');
      addDebugLog('success', 'auth', '[Register] ✅ Magic Link sent successfully via Brevo', {
        email: magicLinkEmail,
        email_id: responseData.email_id,
        actual_flow: responseData.actual_flow,
        user_exists: responseData.user_exists,
        debug: responseData.debug
      });
      
      // Show success message
      console.log('✅ Magic Link Success:', responseData);
      
      setRegisteredEmail(magicLinkEmail);
      setMagicLinkSent(true);
    } catch (err: any) {
      console.error('[Register] ❌ Magic link error:', err);
      addDebugLog('error', 'auth', '[Register] ❌ Magic link error', {
        error: err.message,
        stack: err.stack
      });
      
      // Show detailed error to user
      setError(err.message || 'Magic Link could not be sent. Please check console for details.');
      
      // Also log to console for easy debugging
      console.error('❌ Magic Link Error:', err);
    } finally {
      setMagicLinkLoading(false);
    }
  };

  // Magic Link Success Screen
  if (magicLinkSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-white to-amber-50 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-check-line text-3xl text-teal-600"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Magic Link gesendet!</h1>
              <p className="text-gray-600">
                Wir haben Ihnen einen Anmeldelink an <strong>{registeredEmail}</strong> gesendet.
              </p>
            </div>

            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                <div className="text-sm text-teal-800">
                  <p className="font-medium mb-2">Nächste Schritte:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Öffnen Sie Ihr E-Mail-Postfach</li>
                    <li>Klicken Sie auf den Anmeldelink</li>
                    <li>Sie werden automatisch angemeldet</li>
                  </ol>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setMagicLinkSent(false);
                setShowMagicLink(false);
                setRegisteredEmail('');
              }}
              className="w-full bg-gradient-to-r from-teal-600 to-teal-700 text-white py-3 rounded-lg font-medium hover:from-teal-700 hover:to-teal-800 transition-all shadow-lg hover:shadow-xl whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
            >
              Zurück zur Registrierung
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Magic Link Form
  if (showMagicLink) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-white to-amber-50 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-magic-line text-3xl text-teal-600"></i>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Magic Link Registrierung</h1>
              <p className="text-gray-600">Registrieren Sie sich ohne Passwort</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <i className="ri-error-warning-line text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleMagicLinkSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-Mail-Adresse
                </label>
                <input
                  type="email"
                  value={magicLinkEmail}
                  onChange={(e) => setMagicLinkEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  placeholder="ihre@email.de"
                />
              </div>

              <button
                type="submit"
                disabled={magicLinkLoading}
                className="w-full bg-gradient-to-r from-teal-600 to-teal-700 text-white py-3 rounded-lg font-medium hover:from-teal-700 hover:to-teal-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
              >
                {magicLinkLoading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                    <span>Wird gesendet...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-mail-send-line text-xl"></i>
                    <span>Magic Link senden</span>
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setShowMagicLink(false)}
                className="text-gray-600 hover:text-gray-800 text-sm font-medium cursor-pointer"
              >
                ← Zurück zur normalen Registrierung
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal Registration Form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Konto erstellen</h1>
            <p className="text-gray-600">Starten Sie jetzt mit der Lead-Generierung</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <i className="ri-error-warning-line text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                E-Mail-Adresse
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="ihre@email.de"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Passwort
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Mindestens 6 Zeichen"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Passwort bestätigen
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Passwort wiederholen"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-teal-600 to-teal-700 text-white py-3 rounded-lg font-medium hover:from-teal-700 hover:to-teal-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-xl"></i>
                  <span>Wird registriert...</span>
                </>
              ) : (
                <span>Registrieren</span>
              )}
            </button>
          </form>

          <div className="mt-6">
            <button
              onClick={() => setShowMagicLink(true)}
              disabled={loading}
              className="w-full py-3 px-4 bg-white border-2 border-slate-200 text-slate-700 rounded-lg font-medium hover:border-slate-300 hover:bg-slate-50 transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-mail-send-line text-lg"></i>
              <span>Mit Magic Link registrieren</span>
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Bereits ein Konto?{' '}
              <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer">
                Jetzt anmelden
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm cursor-pointer">
              ← Zurück zur Startseite
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
